import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiUploadCloud, FiX, FiCheckCircle } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const ExcelMigrations = () => {
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeAPI.excelMigrations.list();
      setMigrations(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load migrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const signOff = async (migration) => {
    const notes = window.prompt('Sign-off notes (explain the variance)?');
    if (notes === null) return;
    setBusyId(migration.id);
    setError(null);
    try { await financeAPI.excelMigrations.signOff(migration.id, notes); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to sign off.'); }
    finally { setBusyId(null); }
  };

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiUploadCloud size={22} />Excel Migrations</h1>
          <p style={fs.pageSubtitle}>
            Imported only via the <code>migrate_from_excel</code> management command — this page is for review and sign-off, not upload.
          </p>
        </div>
        <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : migrations.length === 0 ? (
        <div style={fs.emptyState}><FiUploadCloud size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No Excel migrations yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {migrations.map((m) => {
            const busy = busyId === m.id;
            const expanded = expandedId === m.id;
            return (
              <div key={m.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : m.id)}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{m.source_filename}</span>
                      <span style={fs.badge(m.is_clean ? fs.colors.green : fs.colors.amber)}>{m.is_clean ? 'CLEAN' : 'VARIANCE'}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      As of {m.migration_date} · imported {new Date(m.created_at).toLocaleString()} · variance {m.total_variance}
                    </div>
                    {m.signed_off_by && (
                      <div style={{ color: fs.colors.green, fontSize: 12, marginTop: 6 }}>Signed off: {m.sign_off_notes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                    <a href={m.source_file} target="_blank" rel="noreferrer" style={{ ...fs.neutralButton, textDecoration: 'none' }}>Original File</a>
                    {m.has_variance && !m.signed_off_by && (
                      <button onClick={() => signOff(m)} disabled={busy} style={fs.primaryButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Sign Off Variance</button>
                    )}
                  </div>
                </div>
                {expanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${fs.colors.cardBorder}` }}>
                    <table style={{ width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Account</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>CSV Stated</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Ledger Actual</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.reconciliation_lines.map((l) => (
                          <tr key={l.id}>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{l.account_code ? `${l.account_code} — ${l.account_name}` : l.account}</td>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{l.csv_stated_balance}</td>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{l.ledger_actual_balance}</td>
                            <td style={{ padding: '4px 8px', color: parseFloat(l.variance) === 0 ? fs.colors.green : fs.colors.amber }}>{l.variance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExcelMigrations;
